import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { DraftCarton, PickOrderLine, PickedQuantities } from '../draft'
import { PackItem, cartonBreakdown, cartonCapacityFor, packItems, setCartonQuantity } from '../packing'
import AddToCartonControl from './AddToCartonControl'
import CartonNavigator from './CartonNavigator'
import QuantityStepper from './QuantityStepper'

export type PackStepProps = {
	lines: PickOrderLine[],
	picked: PickedQuantities,
	cartons: DraftCarton[],
	activeCartonKey: string | null,
	onActiveCartonChange: ( key: string ) => void,
	onAddCarton: () => void,
	onRemoveCarton: ( key: string ) => void,
	/** Merges absolute per-line quantities into one carton. */
	onCartonContentsChange: ( key: string, contents: Record<string, number> ) => void,
	onTrackingNumberChange: ( key: string, trackingNumber: string ) => void,
}

/** Room for four digits in a carton's quantity field. */
const CARTON_QUANTITY_WIDTH = 104

/** The sku, with the description beneath it — the order the Pick step reads in. */
function LineIdentity( { item }: { item: PackItem } ) {
	const { sku, madeBy, description } = item.identity
	return (
		<Box sx={{ minWidth: 0 }}>
			<Typography
				variant="body1"
				sx={{
					fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
					fontWeight: 700,
					wordBreak: 'break-all',
				}}
			>
				{sku ?? 'No part number'}
			</Typography>
			<Typography variant="body2" color="text.secondary">
				{description}{madeBy ? ` · ${madeBy}` : ''}
			</Typography>
		</Box>
	)
}

/**
 * Packing is done by product, not by fulfillment order.
 *
 * A pick order line is one fulfillment order's claim on a product, so two orders
 * wanting the same product arrive as two lines. A packer holding one bin of products
 * should not have to split them between orders by hand, so the bench groups by
 * product and the app allocates each unit to an order — oldest first, see
 * `allocateIntoCarton`. The allocation is shown on every carton row, because a
 * packer cannot check what they cannot see.
 *
 * One carton is open at a time. Stacking every carton on the page put the same
 * product list on screen once per box and did not survive a run of any size.
 */
export default function PackStep( {
	lines,
	picked,
	cartons,
	activeCartonKey,
	onActiveCartonChange,
	onAddCarton,
	onRemoveCarton,
	onCartonContentsChange,
	onTrackingNumberChange,
}: PackStepProps ) {
	const items = packItems( lines, picked, cartons, activeCartonKey )
	const activeCarton = cartons.find( ( carton ) => carton.key === activeCartonKey )
	const activeIndex = cartons.findIndex( ( carton ) => carton.key === activeCartonKey )
	const cartonName = `Carton ${activeIndex + 1}`
	const inCarton = items.filter( ( item ) => item.inThisCarton > 0 )
	const unitsInCarton = inCarton.reduce( ( sum, item ) => sum + item.inThisCarton, 0 )

	if ( items.length === 0 ) {
		return (
			<Alert severity="warning">
				Nothing was picked. Go back to Pick, or continue to Review to short the whole order.
			</Alert>
		)
	}

	const addToCarton = ( item: PackItem, units: number ) => {
		if ( activeCarton ) {
			onCartonContentsChange( activeCarton.key, setCartonQuantity( item, item.inThisCarton + units ) )
		}
	}

	return (
		<Stack spacing={2}>
			<Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
				{/* The bench: what is in the packer's hands, by product. */}
				<Paper
					variant="outlined"
					sx={{ p: 2.5, flex: 1.2, width: '100%', minWidth: 0, borderColor: 'pickFlow.cardBorder', bgcolor: 'pickFlow.surface' }}
				>
					<Typography variant="h6" sx={{ mb: 1.5 }}>Left to pack</Typography>
					<Stack spacing={1.5}>
						{items.map( ( item ) => {
							return (
								<Stack
									key={item.productId}
									direction={{ xs: 'column', sm: 'row' }}
									spacing={1.5}
									alignItems={{ xs: 'stretch', sm: 'center' }}
									sx={{
										p: 1.5,
										border: 1,
										borderRadius: 1,
										borderColor: 'pickFlow.cardBorder',
										bgcolor: item.remaining === 0 ? 'pickFlow.stepCompleteBackground' : 'transparent',
									}}
								>
									<Box sx={{ minWidth: 72, textAlign: 'center', flexShrink: 0 }}>
										<Typography
											variant="h5"
											color={item.remaining === 0 ? 'success.main' : 'text.primary'}
										>
											{item.remaining}
										</Typography>
										<Typography variant="body2" color="text.secondary">of {item.inHand}</Typography>
									</Box>

									<Box sx={{ flex: 1, minWidth: 0 }}>
										<LineIdentity item={item} />
										{item.remaining === 0 && (
											<Typography variant="body2" color="success.main" sx={{ mt: 0.5 }}>All packed</Typography>
										)}
									</Box>

									<AddToCartonControl
										item={item}
										cartonName={cartonName}
										onAdd={( units ) => addToCarton( item, units )}
									/>
								</Stack>
							)
						} )}
					</Stack>
				</Paper>

				{/* The open carton. */}
				<Paper
					variant="outlined"
					sx={{ p: 2.5, flex: 1, width: '100%', minWidth: 0, borderColor: 'pickFlow.cardBorder', bgcolor: 'pickFlow.surface' }}
				>
					<CartonNavigator
						cartons={cartons}
						activeKey={activeCartonKey}
						onSelect={onActiveCartonChange}
						onAdd={onAddCarton}
					/>

					{activeCarton && (
						<>
							<Stack
								direction="row"
								alignItems="center"
								justifyContent="space-between"
								sx={{ mt: 2, mb: 1 }}
							>
								<Stack direction="row" spacing={1} alignItems="baseline">
									<Typography variant="h6">Contents</Typography>
									<Typography variant="body2" color="text.secondary">
										{unitsInCarton} {unitsInCarton === 1 ? 'unit' : 'units'}
									</Typography>
								</Stack>
								<IconButton
									aria-label={`Remove ${cartonName}`}
									disabled={cartons.length <= 1}
									onClick={() => onRemoveCarton( activeCarton.key )}
								>
									<DeleteOutlineIcon />
								</IconButton>
							</Stack>

							{inCarton.length === 0 ? (
								<Box
									sx={{
										p: 3,
										border: 1,
										borderStyle: 'dashed',
										borderRadius: 1,
										borderColor: 'pickFlow.cardBorder',
										textAlign: 'center',
									}}
								>
									<Typography variant="body2" color="text.secondary">
										Empty. Add items from the left.
									</Typography>
								</Box>
							) : (
								<Stack spacing={1.5}>
									{inCarton.map( ( item ) => (
										<Box
											key={item.productId}
											sx={{
												display: 'grid',
												// The stepper takes a fixed width, so every row's lines up
												// whatever the product or the split beneath it.
												gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) auto' },
												columnGap: 1.5,
												rowGap: 0.75,
												alignItems: 'center',
											}}
										>
											<LineIdentity item={item} />
											<QuantityStepper
												label={`${item.identity.description} in ${cartonName}`}
												value={item.inThisCarton}
												min={0}
												max={cartonCapacityFor( item )}
												valueWidth={CARTON_QUANTITY_WIDTH}
												onChange={( next ) => onCartonContentsChange(
													activeCarton.key,
													setCartonQuantity( item, next ),
												)}
											/>
											{/* Which orders these units ship against. Full width and
											    wrapped, so a split across many orders is read whole
											    rather than cut off. */}
											<Typography variant="body2" color="text.secondary" sx={{ gridColumn: '1 / -1' }}>
												{cartonBreakdown( item )}
											</Typography>
										</Box>
									) )}
								</Stack>
							)}

							<TextField
								fullWidth
								label="Tracking number (optional)"
								placeholder="Scan or type after sealing"
								value={activeCarton.trackingNumber}
								onChange={( event ) => onTrackingNumberChange( activeCarton.key, event.target.value )}
								sx={{ mt: 2 }}
							/>
						</>
					)}
				</Paper>
			</Stack>
		</Stack>
	)
}
